import type { ReactNode } from "react";
import type { HelpHubBranding } from "@/types/help-hub/help-hub";
import { hubBrandTheme } from "../brandInk";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface HubSectionProps {
  id?: string;
  title: string;
  /** One line saying what this band is for. Optional — never filler. */
  subtitle?: string;
  genieIcon: GenieIconName;
  branding: HelpHubBranding;
  /** Right-aligned control, e.g. a search box. */
  action?: ReactNode;
  children: ReactNode;
}

/**
 * Shared band shell, so every section of a Genie Page carries the same heading
 * rhythm regardless of which are switched on for a given business.
 */
export const HubSection = ({
  id,
  title,
  subtitle,
  genieIcon,
  branding,
  action,
  children,
}: HubSectionProps) => {
  const theme = hubBrandTheme(branding);
  return (
    <section id={id} className="scroll-mt-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <GenieIcon
            name={genieIcon}
            size={22}
            color={theme.primary}
            secondaryColor={theme.secondary}
            gradientEnabled={theme.gradientOn}
            label=""
          />
          <div>
            <h2 className="text-[19px] font-bold leading-tight text-genie-text-primary">{title}</h2>
            {subtitle && <p className="text-[13px] text-genie-text-muted">{subtitle}</p>}
            {/* A short rule in the customer's colours — the brand echoing through
                every band, not just the hero. */}
            <div
              aria-hidden
              className="mt-1.5 h-[3px] w-10 rounded-full"
              style={{ background: theme.gradient }}
            />
          </div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
};

export default HubSection;
